import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight,
  ArrowUp, 
  MapPin,
  Mail,
  Phone,
  Globe,
  Clock,
  CheckCircle,
  User,
  Building,
  Send,
  Wrench,
  Cpu,
  Settings,
  Factory,
  AlertCircle,
  Loader2
} from 'lucide-react';

// For map integration - OpenStreetMap with Leaflet
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Common components
import Container from './components/common/Container';
import SectionHeading from './components/common/SectionHeading';
import Button from './components/common/Button';

// Sections
import Newsletter from './components/sections/Newsletter';

// Fix for Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ContactPage = () => {
  // State management
  const [scrolled, setScrolled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    interest: 'General Inquiry'
  });
  const [errors, setErrors] = useState({});
  
  // Refs for scroll animations
  const formRef = useRef(null);
  const mapRef = useRef(null);
  const infoRef = useRef(null);

  // Webhook URL
  const WEBHOOK_URL = 'https://hook.eu2.make.com/z11brx8pgor4esjh1rjc7jjkmpperrrt';

  // Interest options
  const interestOptions = [
    { value: 'Industrial Automation', label: 'Industrial Automation' },
    { value: 'PLC Programming', label: 'PLC Programming' },
    { value: 'Smart Factory', label: 'Smart Factory Solutions' },
    { value: 'Control Systems', label: 'Control Systems' },
    { value: 'Industry 4.0', label: 'Industry 4.0 Consulting' },
    { value: 'General Inquiry', label: 'General Inquiry' }
  ];

  // Office location
  const officeLocation = {
    id: 'headquarters',
    name: 'Galo Industries Headquarters',
    address: 'Šibenska ulica 4B, 23000 Zadar, Croatia',
    phone: '+385 98 199 1813',
    email: 'info@galo-industries.com',
    hours: 'Monday - Friday: 8:00 - 16:00 CET',
    coordinates: { lat: 44.119371, lng: 15.231365 }
  };

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Animation on scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" }
    );
    
    const refs = [formRef, mapRef, infoRef];
    
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Clear submit error when user starts typing
    if (submitError) {
      setSubmitError(null);
    }
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Send data to webhook
  const sendToWebhook = async (data) => {
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(data),
      });

      // Handle both successful responses and webhook-specific responses
      if (response.ok || response.status === 200) {
        return { success: true };
      } else {
        throw new Error(`Webhook responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Webhook error:', error);
      throw error;
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Prepare structured data for webhook
      const webhookData = {
        // Form data
        contact: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          company: formData.company.trim() || null,
          phone: formData.phone.trim() || null,
          interest: formData.interest,
          message: formData.message.trim()
        },
        // Metadata
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'Contact Form',
          page: 'contact',
          url: window.location.href,
          userAgent: navigator.userAgent,
          referrer: document.referrer || null,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        // Lead scoring data
        leadData: {
          hasCompany: !!formData.company.trim(),
          hasPhone: !!formData.phone.trim(),
          messageLength: formData.message.trim().length,
          interestCategory: formData.interest,
          priority: formData.interest === 'Industrial Automation' || 
                   formData.interest === 'Smart Factory' ? 'high' : 'medium'
        },
        // Contact preferences
        preferences: {
          contactMethod: formData.phone ? 'phone_and_email' : 'email_only',
          businessHours: true, // Assume business hours contact preference
          followUpWithin: '24_hours'
        }
      };

      console.log('Sending to webhook:', webhookData);

      // Send to webhook
      await sendToWebhook(webhookData);

      // Success - show success message
      setFormSubmitted(true);
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        interest: 'General Inquiry'
      });



    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(
        'There was an issue sending your message. Please try again or contact us directly at info@galo-industries.com'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="font-sans antialiased overflow-x-hidden bg-white">
      {/* Enhanced SEO Metadata */}
      <Helmet>
        <title>Contact Galo Industries | Engineering Services & Automation Solutions</title>
        <meta
          name="description"
          content="Contact Galo Industries for automation solutions, engineering services, and technical consulting. Get in touch with our team for your project needs."
        />
        <meta name="keywords" content="contact industrial automation, PLC programming services contact, factory automation consultation, smart factory implementation contact, industrial control systems inquiry, manufacturing automation experts, automation engineering contact, Galo Industries contact" />
        <link rel="canonical" href="https://galo-industries.com/contact" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        <meta name="theme-color" content="#0891b2" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://galo-industries.com/contact" />
        <meta property="og:title" content="Contact Galo Industries | Industrial Automation Experts" />
        <meta property="og:description" content="Get in touch with our engineering team for automation solutions and technical consulting services." />
        <meta property="og:image" content="https://galo-industries.com/img/contact-hero.jpg" />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Galo Industries",
            "description": "Engineering services, automation solutions, and technical consulting",
            "url": "https://galo-industries.com",
            "telephone": "+385981991813",
            "email": "info@galo-industries.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Šibenska ulica 4B",
              "addressLocality": "Zadar",
              "addressRegion": "Zadar County",
              "postalCode": "23000",
              "addressCountry": "HR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "44.119371",
              "longitude": "15.231365"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "17:00"
            },
            "priceRange": "$$",
            "paymentAccepted": ["Cash", "Credit Card", "Bank Transfer"],
            "areaServed": "Worldwide",
            "knowsAbout": ["Industrial Automation", "PLC Programming", "Smart Factory", "Industry 4.0"]
          })}
        </script>
        
        {/* Contact Page Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Galo Industries",
            "description": "Contact form and information for industrial automation inquiries",
            "url": "https://galo-industries.com/contact"
          })}
        </script>
      </Helmet>

      {/* Header */}
      <Header current="contact" />
      
      {/* Enhanced Contact Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 text-white relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.15)_1px,_transparent_1px)] bg-[length:20px_20px]"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-cyan-300/10 to-blue-300/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-500/10 to-indigo-300/5 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        
        <Container className="relative z-10">
          {/* Breadcrumb */}
          <div className="mb-8">
            <div className="flex items-center text-blue-100 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white font-medium">Contact</span>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Get In Touch With Our <span className="relative">
                  <span className="text-cyan-200">Team</span>
                  <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-cyan-400/80 to-blue-300/60 rounded-full"></span>
                </span>
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Connect with our team to discuss your projects, technical requirements, and how we can help optimize your processes.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 transform transition-all duration-300 hover:bg-white/15">
                  <div className="text-white mb-2 flex items-center">
                    <Mail className="w-5 h-5 mr-2" />
                    <span className="font-medium">Email Us</span>
                  </div>
                  <a href="mailto:info@galo-industries.com" className="text-blue-100 hover:text-white transition-colors">
                    info@galo-industries.com
                  </a>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 transform transition-all duration-300 hover:bg-white/15">
                  <div className="text-white mb-2 flex items-center">
                    <Phone className="w-5 h-5 mr-2" />
                    <span className="font-medium">Call Us</span>
                  </div>
                  <a href="tel:+385981991813" className="text-blue-100 hover:text-white transition-colors">
                    +385 98 199 1813
                  </a>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="relative">
                {/* Decorative elements */}
                <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-500/10 blur-xl"></div>
                
                {/* Contact card with 3D effect */}
                <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-8 shadow-xl transform transition-all duration-300">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white mr-4">
                      <Factory className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-white text-xl font-bold">Headquarters</h3>
                      <p className="text-blue-100">Zadar, Croatia</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-4">
                    <li className="flex">
                      <MapPin className="w-5 h-5 text-blue-300 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-blue-100">{officeLocation.address}</span>
                    </li>
                    <li className="flex">
                      <Mail className="w-5 h-5 text-blue-300 mr-3 flex-shrink-0 mt-0.5" />
                      <a href={`mailto:${officeLocation.email}`} className="text-blue-100 hover:text-white transition-colors">
                        {officeLocation.email}
                      </a>
                    </li>
                    <li className="flex">
                      <Phone className="w-5 h-5 text-blue-300 mr-3 flex-shrink-0 mt-0.5" />
                      <a href={`tel:${officeLocation.phone.replace(/\s/g, '')}`} className="text-blue-100 hover:text-white transition-colors">
                        {officeLocation.phone}
                      </a>
                    </li>
                    <li className="flex">
                      <Clock className="w-5 h-5 text-blue-300 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-blue-100">{officeLocation.hours}</span>
                    </li>
                  </ul>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full border-2 border-white/10 rounded-xl -z-10 translate-y-4 translate-x-4"></div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Enhanced Contact Form Section */}
      <section className="py-24 bg-white" ref={formRef}>
        <Container>
          <SectionHeading
            eyebrow="Get In Touch"
            title={<>Let's Discuss Your <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Project</span></>}
            description="Contact our team to discuss your needs and project requirements"
            centered
          />
          
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-100 p-8 lg:p-12 contact-form">
            {formSubmitted ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white mb-6">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Thank You for Your Message!</h3>
                <p className="text-slate-600 mb-8">
                  Our team will review your inquiry and get back to you within 24 business hours.
                </p>
                <button
                  onClick={() => setFormSubmitted(false)}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Error message display */}
                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-red-800 font-medium mb-1">Submission Error</h4>
                      <p className="text-red-700 text-sm">{submitError}</p>
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Name field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                        <User className="w-5 h-5" />
                      </div>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={`w-full py-3 pl-12 pr-4 bg-white border ${
                          errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
                        } rounded-lg shadow-sm focus:outline-none focus:ring-2 transition-colors disabled:bg-slate-50 disabled:text-slate-500`}
                        placeholder="John Doe"
                        aria-label="Your full name"
                      />
                    </div>
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>
                  
                  {/* Email field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                        <Mail className="w-5 h-5" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={`w-full py-3 pl-12 pr-4 bg-white border ${
                          errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
                        } rounded-lg shadow-sm focus:outline-none focus:ring-2 transition-colors disabled:bg-slate-50 disabled:text-slate-500`}
                        placeholder="john@example.com"
                        aria-label="Your email address"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                  
                  {/* Company field */}
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                      Company / Organization
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                        <Building className="w-5 h-5" />
                      </div>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="w-full py-3 pl-12 pr-4 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-slate-50 disabled:text-slate-500"
                        placeholder="Manufacturing Company Inc."
                        aria-label="Your company name"
                      />
                    </div>
                  </div>
                  
                  {/* Phone field */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                        <Phone className="w-5 h-5" />
                      </div>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="w-full py-3 pl-12 pr-4 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-slate-50 disabled:text-slate-500"
                        placeholder="+385 98 123 4567"
                        aria-label="Your phone number"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Interest select field - Enhanced with SEO keywords */}
                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-slate-700 mb-2">
                    What can we help you with?
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-slate-50 disabled:text-slate-500"
                    aria-label="Select your area of interest"
                  >
                    {interestOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Message field */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Project Details <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    rows={6}
                    className={`w-full py-3 px-4 bg-white border ${
                      errors.message ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
                    } rounded-lg shadow-sm focus:outline-none focus:ring-2 transition-colors disabled:bg-slate-50 disabled:text-slate-500`}
                    placeholder="Please describe your project, technical requirements, or any questions you have..."
                    aria-label="Describe your project or inquiry"
                  ></textarea>
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                </div>
                
                {/* Submit button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium rounded-lg shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                    aria-label="Send your message"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span className="mr-2">Send Message</span>
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Container>
      </section>
      
      {/* Office Location Map Section with OpenStreetMap */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50" ref={mapRef}>
        <Container>
          <SectionHeading
            eyebrow="Location"
            title={<>Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Office</span></>}
            description="Visit our HQ in Zadar, Croatia"
            centered
          />
          
          <div className="grid md:grid-cols-12 gap-6 max-w-6xl mx-auto map-section">
            {/* Office location card - enhanced with keywords */}
            <div className="md:col-span-4">
              <div 
                className="bg-white rounded-xl shadow-xl border border-slate-100 p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 office-card"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center">
                  <Factory className="w-5 h-5 text-cyan-700 mr-2" />
                  {officeLocation.name}
                </h3>
                
                <ul className="space-y-3 mt-4">
                  <li className="flex">
                    <MapPin className="w-4 h-4 text-slate-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{officeLocation.address}</span>
                  </li>
                  <li className="flex">
                    <Mail className="w-4 h-4 text-slate-400 mr-3 flex-shrink-0 mt-0.5" />
                    <a href={`mailto:${officeLocation.email}`} className="text-slate-600 hover:text-cyan-700 transition-colors">
                      {officeLocation.email}
                    </a>
                  </li>
                  <li className="flex">
                    <Phone className="w-4 h-4 text-slate-400 mr-3 flex-shrink-0 mt-0.5" />
                    <a href={`tel:${officeLocation.phone.replace(/\s/g, '')}`} className="text-slate-600 hover:text-cyan-700 transition-colors">
                      {officeLocation.phone}
                    </a>
                  </li>
                  <li className="flex">
                    <Clock className="w-4 h-4 text-slate-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{officeLocation.hours}</span>
                  </li>
                </ul>
                
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <a 
                    href={`https://maps.google.com/?q=${officeLocation.coordinates.lat},${officeLocation.coordinates.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    View on Google Maps
                  </a>
                </div>
              </div>
            </div>
            
            {/* Interactive OpenStreetMap */}
            <div className="md:col-span-8 bg-white rounded-xl overflow-hidden shadow-xl border border-slate-100 h-full min-h-[500px] map-container">
              <MapContainer
                center={[officeLocation.coordinates.lat, officeLocation.coordinates.lng]}
                zoom={15}
                scrollWheelZoom={false}
                className="h-full w-full"
                style={{ minHeight: '500px' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[officeLocation.coordinates.lat, officeLocation.coordinates.lng]}>
                  <Popup>
                    <div className="text-center p-2">
                      <h4 className="font-bold text-slate-900 mb-1">Galo Industries</h4>
                      <p className="text-sm text-slate-600">Services</p>
                      <p className="text-xs text-slate-500">Šibenska ulica 4B, Zadar</p>
                    </div>
                  </Popup>
                </Marker>
                <ZoomControl position="bottomright" />
                
                {/* Map overlay with industrial automation focus */}
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 max-w-xs bg-white rounded-lg shadow-lg p-3 sm:p-4 border border-slate-100 z-[1000]">
                  <div className="flex items-center mb-1 sm:mb-2">
                    <Globe className="w-4 sm:w-5 h-4 sm:h-5 text-cyan-700 mr-1.5 sm:mr-2" />
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base">Global Coverage</h4>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600">
                    From our Zadar headquarters, we provide services to clients worldwide.
                  </p>
                </div>
              </MapContainer>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Enhanced Contact Info & FAQ Section */}
      <section className="py-24 bg-white" ref={infoRef}>
        <Container>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto contact-info">
            {/* General contact info - enhanced with SEO keywords */}
            <div>
              <div className="inline-flex items-center mb-3">
                <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
                <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3">Contact Info</span>
              </div>
              
              <h2 className="text-3xl font-bold text-slate-900 mb-6 leading-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Services</span>
              </h2>
              
              <p className="text-slate-600 mb-8">
                Whether you need technical consulting, project development, or engineering support, our team is ready to help with your automation and manufacturing needs.
              </p>
              
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 shadow-lg border border-slate-100 mb-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                  <Globe className="w-5 h-5 text-cyan-700 mr-2" />
                  Global Coverage
                </h3>
                <p className="text-slate-600 mb-4">
                  From our Croatian headquarters, we serve clients worldwide, providing both on-site and remote engineering support.
                </p>
                <a 
                  href="mailto:info@galo-industries.com"
                  className="inline-flex items-center bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Our Team
                </a>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Connect With Us</h3>
                <p className="text-slate-600 mb-4">
                  Follow our latest projects and insights on LinkedIn.
                </p>
                <a 
                  href="https://linkedin.com/company/galo-industries" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  LinkedIn: Galo Industries
                </a>
              </div>
            </div>
            
            {/* Enhanced FAQs with SEO keywords */}
            <div>
              <div className="inline-flex items-center mb-3">
                <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
                <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3">FAQs</span>
              </div>
              
              <h2 className="text-3xl font-bold text-slate-900 mb-6 leading-tight">
                Frequently Asked <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Questions</span>
              </h2>
              
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-all duration-300">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">How quickly can you start a project?</h3>
                  <p className="text-slate-600">
                    We typically respond to inquiries within 24 hours. Project timeline depends on scope, but we can often begin initial planning or programming within 1-2 weeks.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-all duration-300">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Do you provide remote support?</h3>
                  <p className="text-slate-600">
                    Yes, we offer both on-site services and remote support for clients worldwide.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-all duration-300">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Which PLC brands do you work with?</h3>
                  <p className="text-slate-600">
                    Our engineers have expertise in major PLC platforms including Siemens, Allen-Bradley, ABB, and others. We select the optimal control system based on your specific manufacturing requirements.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-all duration-300">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">What is your Industry 4.0 implementation process?</h3>
                  <p className="text-slate-600">
                    Our smart factory implementation begins with manufacturing assessment, followed by IIoT sensor integration, data analytics setup, and predictive maintenance deployment, all tailored to your production needs.
                  </p>
                </div>
                
                <div className="mt-6">
                  <a 
                    href="/faq"
                    className="inline-flex items-center border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-500 hover:text-white font-medium px-6 py-3 rounded-lg transition-all duration-300"
                  >
                    View All FAQs
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Newsletter - using imported component */}
      <Newsletter />
      
      {/* Footer - using imported component */}
      <Footer />
      
      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-20 hover:shadow-xl"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
      
      <style jsx>{`
        /* Animation for slow pulse */
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 0.3; }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 6s ease-in-out infinite;
        }
        
        /* Hide scrollbar */
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Scroll animation classes */
        .contact-form,
        .map-section,
        .contact-info {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        
        .is-visible .contact-form,
        .is-visible .map-section,
        .is-visible .contact-info {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Card animations */
        .office-card {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.5s forwards;
        }
        
        .is-visible .office-card {
          animation-delay: 0.1s;
        }
        
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Leaflet specific styles to fix z-index issues */
        .leaflet-container {
          z-index: 1;
        }
        
        .leaflet-top, .leaflet-bottom {
          z-index: 10;
        }
        
        .leaflet-control {
          z-index: 10;
        }
      `}</style>
    </div>
  );
};

export default ContactPage